// v3 bakery, authored the way age-of-mcp writes it: local field helpers + the
// shared styleV3 part makers, instead of a flat wall of literals. A limewashed
// bakehouse under a wheat-gold (ROOF_FOOD) gable fills the west half, its stone
// chimney poking through and a glowing oven mouth opening onto the forecourt;
// the east half is an open shop yard with a loaf counter and an oversized loaf
// sign read from above. Ported 1:1 from age-of-mcp's v3/bakery.ts.
import * as THREE from "three";
import { defineModel, benchWork, styleV3, type Part } from "../../lib";

const { box, cyl, gable, poles, slab, FIRE, PLASTER, ROOF_FOOD, STONE, STONE_DK, TIMBER, WOOD } = styleV3;

const BASE = 0.12;                       // foundation slab top
const BX = -0.45, BW = 0.95, BD = 1.5;   // the bakehouse, west half of the plot
const FRONT = BD / 2;                    // its +z door face
const WALL_H = 0.7;

// The bakehouse walls, `h` tall on the foundation.
function bakehouse(h: number): Part {
  return box(BW, h, BD, PLASTER, [BX, BASE + h / 2, 0]);
}

// The big gable roof, ridge along z — ONE part, the food-chain gold code.
function roof(): Part {
  return gable(BW + 0.14, BD + 0.16, 0.45, ROOF_FOOD, BX, BASE + WALL_H, 0, "z");
}

// The stone oven chimney at the back, poking up through the roof.
function chimney(h: number): Part {
  return box(0.18, h, 0.18, STONE, [BX + 0.26, BASE + h / 2, -0.48]);
}

// The plank door on the bakehouse front.
function door(): Part {
  return box(0.26, 0.5, 0.05, TIMBER, [BX - 0.22, BASE + 0.25, FRONT + 0.02]);
}

// The glowing oven mouth opening onto the open forecourt: dark arch + fire.
function ovenMouth(): Part[] {
  return [
    box(0.36, 0.34, 0.07, STONE_DK, [BX + 0.2, BASE + 0.3, FRONT + 0.02]),
    box(0.26, 0.22, 0.06, FIRE,     [BX + 0.2, BASE + 0.28, FRONT + 0.06]),
  ];
}

// A golden loaf — a squashed, stretched sphere.
function loaf(x: number, y: number, z: number, r: number): Part {
  return {
    geometry: new THREE.SphereGeometry(r, 7, 5), color: ROOF_FOOD,
    position: [x, y, z], scale: [1, 0.65, 1.5],
  };
}

// The OVERSIZED loaf sign raised on a post at the shop-yard corner — the icon.
function loafSign(): Part[] {
  return [
    cyl(0.03, 0.035, 0.62, 5, TIMBER, [0.62, BASE + 0.31, 0.62]),
    loaf(0.62, BASE + 0.72, 0.62, 0.17),
  ];
}

// The open shop counter facing the road, with loaves laid out.
function counter(): Part[] {
  const parts: Part[] = [
    box(0.7, 0.1, 0.24, WOOD, [0.45, BASE + 0.38, 0.32]),
    box(0.7, 0.28, 0.06, WOOD, [0.45, BASE + 0.19, 0.41]),
  ];
  for (let i = 0; i < 3; i++) {
    parts.push(loaf(0.25 + i * 0.2, BASE + 0.49, 0.32, 0.07));
  }
  return parts;
}

// Flour sacks stacked in the back yard.
function sacks(): Part[] {
  return [
    box(0.18, 0.2, 0.18, PLASTER, [0.34, BASE + 0.1, -0.5], [0, 0.25, 0]),
    box(0.16, 0.17, 0.16, PLASTER, [0.52, BASE + 0.085, -0.38], [0, -0.2, 0]),
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(1.94, 1.94)];
  if (s === 1) {
    return [slab(1.94, 1.94), bakehouse(0.35), chimney(0.6), ...poles(1.7, 1.7, 0.85)];
  }
  if (s === 2) {
    return [
      slab(1.94, 1.94), bakehouse(WALL_H), chimney(1.1),
      door(), ...ovenMouth(), ...poles(1.7, 1.7, 1.4),
    ];
  }
  return [
    slab(1.94, 1.94), bakehouse(WALL_H), roof(), chimney(1.43),
    door(), ...ovenMouth(), ...loafSign(), ...counter(), ...sacks(),
  ];
}

export const bakeryV3 = defineModel({
  id: "bakery-v3",
  tiles: { w: 2, h: 2 },
  batchId: "45-optimized",
  name: "Bakery (v3)",
  tags: ["building","bakery","low-poly","age-of-mcp","v3"],
  summary: "Low-poly bakery.",
  description: "A baker's oven that bakes flour and firewood into bread.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "bench-a", position: [-0.25, 0.12, 1.02], facing: Math.PI },
    { id: "bench-b", position: [0.45, 0.12, 0.08], facing: 0 },
  ],
  interactions: [
    { id: "bake", label: "At the oven", station: "bench-a", variants: ["complete"], ...benchWork() },
  ],
});
